import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CalculateIcon from "@mui/icons-material/Calculate";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import {useCallback, useEffect, useRef, useState} from "react";
import {Product} from "../../product/product.entity";
import {productRepository} from "../../product/product.repository";
import {aiSellCopilot, type CopilotChatBlock, type CopilotInsightCard} from "../../ai/callable-ai";
import {isInsufficientAiCreditsError} from "../../ai/ai-credit-guard";
import type {Customer} from "../../customer/customer.entity";

// ─── Mode detection ────────────────────────────────────────────────────────────

type InputMode = "barcode" | "search" | "ai";

function detectMode(value: string): InputMode {
  const v = value.trim();
  if (!v) return "search";
  if (/^\d{4,}$/.test(v)) return "barcode";
  return "search";
}

// ─── Mode chip ────────────────────────────────────────────────────────────────

const MODE_CONFIG: Record<InputMode, {label: string; color: string; icon: React.ReactElement}> = {
  barcode: {label: "Código", color: "#3b82f6", icon: <QrCodeScannerIcon sx={{fontSize: 12}} />},
  search: {label: "Busca", color: "#6366f1", icon: <SearchIcon sx={{fontSize: 12}} />},
  ai: {label: "IA", color: "#0d9488", icon: <AutoAwesomeIcon sx={{fontSize: 12}} />},
};

// ─── Inline AI response ───────────────────────────────────────────────────────

type AiResponse = {
  insights: CopilotInsightCard[];
  chatBlocks: CopilotChatBlock[];
};

const AiResponseCard = ({
  response,
  onAdd,
  onAddUnregistered,
  onClose,
}: {
  response: AiResponse;
  onAdd: (productId: string) => void;
  onAddUnregistered: (name: string, price: number) => void;
  onClose: () => void;
}) => {
  if (response.insights.length === 0 && response.chatBlocks.length === 0) return null;
  return (
    <Paper
      elevation={4}
      sx={{
        position: "absolute",
        top: "calc(100% + 6px)",
        left: 0,
        right: 0,
        zIndex: 1400,
        borderRadius: 2,
        p: 1.5,
        bgcolor: "#0f172a",
        border: "1px solid rgba(13,148,136,0.35)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 1}}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <AutoAwesomeIcon sx={{fontSize: 13, color: "#0d9488"}} />
          <Typography sx={{fontSize: 11, fontWeight: 700, color: "#2dd4bf", textTransform: "uppercase", letterSpacing: "0.06em"}}>
            Copiloto
          </Typography>
        </Stack>
        <Button size="small" onClick={onClose} sx={{minWidth: 0, fontSize: 10, color: "rgba(248,250,252,0.45)", py: 0, px: 0.75}}>
          fechar
        </Button>
      </Stack>

      {response.chatBlocks.map((block, i) => {
        if (block.kind === "info") {
          return (
            <Box key={i} sx={{
              bgcolor: block.highlight ? "rgba(13,148,136,0.18)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${block.highlight ? "rgba(45,212,191,0.35)" : "rgba(255,255,255,0.10)"}`,
              borderLeft: block.highlight ? "3px solid #2dd4bf" : "1px solid rgba(255,255,255,0.10)",
              borderRadius: "8px", px: 1.5, py: 0.75, mb: 0.75,
            }}>
              <Typography sx={{color: "rgba(248,250,252,0.55)", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em"}}>
                {block.title}
              </Typography>
              <Typography sx={{color: "#f8fafc", fontWeight: 700, fontSize: 15}}>
                {block.infoValue}
              </Typography>
            </Box>
          );
        }
        if (block.kind === "suggestion" && block.productId) {
          return (
            <Box key={i} sx={{
              bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)",
              borderRadius: "8px", px: 1.5, py: 0.75, mb: 0.75,
              display: "flex", alignItems: "center", gap: 1,
            }}>
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography sx={{color: "#2dd4bf", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em"}}>
                  {block.title}
                </Typography>
                <Typography sx={{color: "#f8fafc", fontSize: 12}} noWrap>{block.subtitle}</Typography>
              </Box>
              <Button size="small" onClick={() => onAdd(block.productId!)} sx={{
                fontSize: 11, fontWeight: 700, color: "#2dd4bf",
                border: "1px solid rgba(45,212,191,0.35)", borderRadius: "7px",
                px: 1, py: 0.2, flexShrink: 0, minWidth: 0,
                "&:hover": {bgcolor: "rgba(45,212,191,0.12)"},
              }}>
                + Add
              </Button>
            </Box>
          );
        }
        if (block.kind === "action" && block.pdvAction === "add_unregistered") {
          return (
            <Box key={i} sx={{
              bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)",
              borderRadius: "8px", px: 1.5, py: 0.75, mb: 0.75,
              display: "flex", alignItems: "center", gap: 1,
            }}>
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography sx={{color: "#2dd4bf", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em"}}>
                  Venda Avulsa
                </Typography>
                <Typography sx={{color: "#f8fafc", fontSize: 12}} noWrap>{block.title}</Typography>
              </Box>
              <Button size="small" onClick={() => onAddUnregistered(block.newProductName || "Produto", block.newProductPrice || 0)} sx={{
                fontSize: 11, fontWeight: 700, color: "#2dd4bf",
                border: "1px solid rgba(45,212,191,0.35)", borderRadius: "7px",
                px: 1, py: 0.2, flexShrink: 0, minWidth: 0,
                "&:hover": {bgcolor: "rgba(45,212,191,0.12)"},
              }}>
                + Lançar
              </Button>
            </Box>
          );
        }
        return null;
      })}

      {response.insights.map((card, i) => (
        <Box key={i} sx={{
          display: "flex", alignItems: "center", gap: 1,
          bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(45,212,191,0.2)",
          borderLeft: "3px solid #0d9488",
          borderRadius: "8px", px: 1.5, py: 0.75, mb: 0.75,
        }}>
          <Box sx={{flex: 1, minWidth: 0}}>
            <Typography sx={{color: "#f8fafc", fontWeight: 600, fontSize: 12}} noWrap>{card.title}</Typography>
            <Typography sx={{color: "rgba(248,250,252,0.55)", fontSize: 11}} noWrap>{card.subtitle}</Typography>
          </Box>
          <Button size="small" onClick={() => onAdd(card.productId)} sx={{
            fontSize: 11, fontWeight: 700, color: "#2dd4bf",
            border: "1px solid rgba(45,212,191,0.35)", borderRadius: "7px",
            px: 1, py: 0.2, flexShrink: 0, minWidth: 0,
            "&:hover": {bgcolor: "rgba(45,212,191,0.12)"},
          }}>
            + Add
          </Button>
        </Box>
      ))}
    </Paper>
  );
};

export type DropdownItem =
  | { id: string; type: "product"; product: Product }
  | { id: string; type: "search_more"; term: string }
  | { id: string; type: "register_new"; term: string }
  | { id: string; type: "select_customer"; customer: Customer }
  | { id: string; type: "ai"; term: string }
  | { id: string; type: "add_single_value"; price: number }
  | { id: string; type: "add_sum_value"; total: number }
  | { id: string; type: "add_multiple_values"; prices: number[] };

function parseMathValues(term: string) {
  const trimmed = term.trim();
  if (!trimmed) return null;

  const singleRegex = /^\d+([.,]\d{1,2})?$/;
  if (singleRegex.test(trimmed)) {
    return { type: 'single' as const, price: parseFloat(trimmed.replace(',', '.')) };
  }

  const multiRegex = /^(\d+([.,]\d{1,2})?[\s+]+)+\d+([.,]\d{1,2})?$/;
  if (multiRegex.test(trimmed)) {
    const parts = trimmed.split(/[\s+]+/).map(p => parseFloat(p.replace(',', '.')));
    const sum = parts.reduce((a,b) => a+b, 0);
    return { type: 'multiple' as const, prices: parts, total: sum };
  }

  return null;
}

function matchCustomers(term: string, customers: Customer[]): Customer[] {
  const t = term.trim().toLowerCase();
  if (t.length < 2) return [];
  return customers
    .filter(
      (c) =>
        c.getName().toLowerCase().includes(t) ||
        c.getPhone().replace(/\D/g, "").includes(t.replace(/\D/g, ""))
    )
    .slice(0, 2);
}

const getDropdownItems = (
  term: string,
  results: Product[],
  loading: boolean,
  customers: Customer[] = []
): DropdownItem[] => {
  const items: DropdownItem[] = [];
  const t = term.trim();
  if (!t) return items;

  if (!loading && results.length > 0) {
    items.push({ id: `prod-${results[0].id}`, type: "product", product: results[0] });
  }

  const math = parseMathValues(t);
  if (math?.type === "single") {
    items.push({ id: "single_val", type: "add_single_value", price: math.price });
  } else if (math?.type === "multiple") {
    items.push({ id: "sum_val", type: "add_sum_value", total: math.total });
    items.push({ id: "multi_val", type: "add_multiple_values", prices: math.prices });
  }

  items.push({ id: "search_more", type: "search_more", term: t });
  items.push({ id: "register_new", type: "register_new", term: t });

  const matched = matchCustomers(t, customers);
  for (const c of matched) {
    items.push({ id: `cust-${c.id}`, type: "select_customer", customer: c });
  }

  items.push({ id: "ai", type: "ai", term: t });

  return items;
};

const SearchDropdown = ({
  items,
  loading,
  term,
  activeIndex,
  onAction,
}: {
  items: DropdownItem[];
  loading: boolean;
  term: string;
  activeIndex: number;
  onAction: (item: DropdownItem) => void;
}) => (
  <Paper
    elevation={4}
    sx={{
      position: "absolute",
      top: "calc(100% + 4px)",
      left: 0,
      right: 0,
      zIndex: 1400,
      borderRadius: 2,
      overflow: "hidden",
      maxHeight: 280,
      overflowY: "auto",
      "&::-webkit-scrollbar": {width: 4},
      "&::-webkit-scrollbar-thumb": {background: "rgba(0,0,0,0.15)", borderRadius: 4},
    }}
  >
    {loading && (
      <Box sx={{display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper"}}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary" fontWeight={500}>Buscando produtos…</Typography>
      </Box>
    )}
    {items.length === 0 && !loading && term.length >= 2 && (
      <Box sx={{px: 2, py: 1.5}}>
        <Typography variant="caption" color="text.secondary" display="block">
          Aguardando ação para "{term}"
        </Typography>
      </Box>
    )}
    {items.map((item, i) => {
        const isSelected = i === activeIndex;
        const sx = {
          px: 2, py: 1, cursor: "pointer",
          bgcolor: isSelected ? "action.selected" : "background.paper",
          "&:hover": {bgcolor: "action.hover"},
          borderBottom: i < items.length - 1 ? "1px solid" : "none",
          borderColor: "divider",
        };

        if (item.type === "product") {
           return (
              <Box key={item.id} onClick={() => onAction(item)} sx={sx}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{minWidth: 0, flex: 1}}>
                    <Typography variant="body2" fontWeight={600} noWrap>{item.product.getName()}</Typography>
                    {item.product.getCode() ? (
                      <Typography variant="caption" color="text.secondary">{item.product.getCode()}</Typography>
                    ) : null}
                  </Box>
                  <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ml: 1, flexShrink: 0}}>
                    R$ {item.product.getPrice().toFixed(2).replace(".", ",")}
                  </Typography>
                </Stack>
              </Box>
           );
        }
        if (item.type === "search_more") {
           return (
              <Box key={item.id} onClick={() => onAction(item)} sx={sx}>
                 <Stack direction="row" alignItems="center" spacing={1.5}>
                    <SearchIcon sx={{fontSize: 18, color: "text.secondary"}} />
                    <Typography variant="body2">Buscar <b>"{item.term}"</b> por nome/descrição</Typography>
                 </Stack>
              </Box>
           );
        }
        if (item.type === "register_new") {
           return (
              <Box key={item.id} onClick={() => onAction(item)} sx={sx}>
                 <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AddCircleOutlineIcon sx={{fontSize: 18, color: "text.secondary"}} />
                    <Typography variant="body2">Cadastrar <b>"{item.term}"</b> como item</Typography>
                 </Stack>
              </Box>
           );
        }
        if (item.type === "select_customer") {
           return (
              <Box key={item.id} onClick={() => onAction(item)} sx={sx}>
                 <Stack direction="row" alignItems="center" spacing={1.5}>
                    <PersonIcon sx={{fontSize: 18, color: "text.secondary"}} />
                    <Box sx={{flex: 1, minWidth: 0}}>
                       <Typography variant="body2" fontWeight={600} noWrap>
                         {item.customer.getName()}
                       </Typography>
                       {item.customer.getPhone() ? (
                         <Typography variant="caption" color="text.secondary">{item.customer.getPhone()}</Typography>
                       ) : null}
                    </Box>
                    <Chip label="Cliente" size="small" variant="outlined" sx={{fontSize: 10, height: 18}} />
                 </Stack>
              </Box>
           );
        }
        if (item.type === "ai") {
           return (
              <Box key={item.id} onClick={() => onAction(item)} sx={sx}>
                 <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AutoAwesomeIcon sx={{fontSize: 18, color: "#0d9488"}} />
                    <Typography variant="body2" sx={{color: "#0d9488", fontWeight: 600}}>
                      Perguntar ao assistente sobre <b>"{item.term}"</b>
                    </Typography>
                 </Stack>
              </Box>
           );
        }
        if (item.type === "add_single_value") {
           return (
              <Box key={item.id} onClick={() => onAction(item)} sx={sx}>
                 <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AttachMoneyIcon sx={{fontSize: 18, color: "text.secondary"}} />
                    <Typography variant="body2">Adicionar <b>{item.price.toFixed(2)}</b> como valor avulso</Typography>
                 </Stack>
              </Box>
           );
        }
        if (item.type === "add_sum_value") {
           return (
              <Box key={item.id} onClick={() => onAction(item)} sx={sx}>
                 <Stack direction="row" alignItems="center" spacing={1.5}>
                    <CalculateIcon sx={{fontSize: 18, color: "text.secondary"}} />
                    <Typography variant="body2">Adicionar <b>{item.total.toFixed(2)}</b> como venda avulsa</Typography>
                 </Stack>
              </Box>
           );
        }
        if (item.type === "add_multiple_values") {
           return (
              <Box key={item.id} onClick={() => onAction(item)} sx={sx}>
                 <Stack direction="row" alignItems="center" spacing={1.5}>
                    <ReceiptIcon sx={{fontSize: 18, color: "text.secondary"}} />
                    <Typography variant="body2">Adicionar <b>{item.prices.map(p => p.toFixed(2)).join(", ")}</b> como valores individuais</Typography>
                 </Stack>
              </Box>
           );
        }
        return null;
      })}
  </Paper>
);

// ─── Props ────────────────────────────────────────────────────────────────────

type SmartSellInputProps = {
  sellId: string;
  removeMode: boolean;
  customers?: Customer[];
  onAddByCode: (code: string) => Promise<void>;
  onAddProductById: (productId: string) => Promise<void>;
  onSearchMore: (term: string) => void;
  onRegisterNew: (term: string) => void;
  onAddOnlyPrice: () => void;
  onAddUnregistered: (name: string, price: number) => void;
  onFocusLock: (locked: boolean) => void;
  onSelectCustomer?: (customer: Customer) => Promise<void>;
};

// ─── Main component ───────────────────────────────────────────────────────────

export const SmartSellInput = ({
  sellId,
  removeMode,
  customers = [],
  onAddByCode,
  onAddProductById,
  onSearchMore,
  onRegisterNew,
  onAddOnlyPrice,
  onAddUnregistered,
  onFocusLock,
  onSelectCustomer,
}: SmartSellInputProps) => {
  const [value, setValue] = useState("");
  const [mode, setMode] = useState<InputMode>("search");
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState<AiResponse | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback((newValue: string) => {
    setValue(newValue);
    setAiResponse(null);
    const m = detectMode(newValue);
    setMode(m);
    setActiveIndex(-1);

    if (m === "search" && newValue.trim().length >= 2) {
      setShowDropdown(true);
      setSearchLoading(true);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          const results = await productRepository.searchProductsForSell(newValue.trim(), 8);
          setSearchResults(results);
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    } else {
      setShowDropdown(false);
      setSearchResults([]);
      setSearchLoading(false);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  }, []);

  const dropdownItems = getDropdownItems(value.trim(), searchResults, searchLoading, customers);

  const submitAi = useCallback(async (customMsg?: string) => {
    const msg = customMsg || value.trim();
    if (!msg || aiLoading) return;
    setValue("");
    setMode("search");
    setAiLoading(true);
    try {
      const res = await aiSellCopilot({sellId, userMessage: msg});
      setAiResponse({insights: res.insights, chatBlocks: res.chatBlocks});
    } catch (e) {
      if (isInsufficientAiCreditsError(e)) return;
    } finally {
      setAiLoading(false);
    }
  }, [value, sellId, aiLoading]);

  const executeAction = useCallback(async (item: DropdownItem) => {
    setShowDropdown(false);
    setValue("");
    setMode("search");

    if (item.type === "product") {
      await onAddProductById(item.product.id);
    } else if (item.type === "search_more") {
      onSearchMore(item.term);
    } else if (item.type === "register_new") {
      onRegisterNew(item.term);
    } else if (item.type === "select_customer") {
      await onSelectCustomer?.(item.customer);
    } else if (item.type === "ai") {
      await submitAi(item.term);
    } else if (item.type === "add_single_value") {
      onAddUnregistered("Produto avulso", item.price);
    } else if (item.type === "add_sum_value") {
      onAddUnregistered("Venda avulsa", item.total);
    } else if (item.type === "add_multiple_values") {
      item.prices.forEach(p => onAddUnregistered("Produto avulso", p));
    }
  }, [onAddProductById, onAddByCode, onRegisterNew, submitAi, onAddUnregistered, onSelectCustomer]);

  const handleKeyDown = useCallback(async (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setShowDropdown(false);
      setAiResponse(null);
      return;
    }

    if (e.key === "ArrowDown" && showDropdown) {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, dropdownItems.length - 1));
      return;
    }

    if (e.key === "ArrowUp" && showDropdown) {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();

      if (showDropdown && activeIndex >= 0 && dropdownItems[activeIndex]) {
        await executeAction(dropdownItems[activeIndex]);
        return;
      }

      if (mode === "ai") {
        await submitAi(value.trim());
        return;
      }

      const v = value.trim();
      if (!v) return;
      
      // If we are in barcode mode, just add
      if (mode === "barcode") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setShowDropdown(false);
        setSearchResults([]);
        setValue("");
        setMode("search");
        await onAddByCode(v);
        return;
      }

      // If we are here and hit Enter without selecting an item, we should probably pick the first item if there is one?
      // Or default to 'search_more'
      if (dropdownItems.length > 0) {
         // pick search_more or product
         const target = dropdownItems.find(i => i.type === "search_more") || dropdownItems[0];
         await executeAction(target);
         return;
      }
    }
  }, [mode, showDropdown, dropdownItems, activeIndex, value, executeAction, submitAi, onAddByCode]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const cfg = MODE_CONFIG[mode];

  return (
    <Box ref={containerRef} sx={{position: "relative"}}>
      <TextField
        id="smart-sell-input"
        fullWidth
        autoFocus
        size="small"
        value={value}
        placeholder={
          removeMode
            ? "Código ou nome para remover"
            : mode === "barcode"
            ? "Pressione Enter para adicionar pelo código"
            : mode === "ai"
            ? "Pressione Enter para enviar ao copiloto"
            : "Código de barras, nome ou comando de IA…"
        }
        color={removeMode ? "error" : "primary"}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={(e) => void handleKeyDown(e)}
        onFocus={() => {
          onFocusLock(true);
          if (mode === "search" && value.trim().length >= 2 && searchResults.length > 0) {
            setShowDropdown(true);
          }
        }}
        onBlur={() => {
          // small delay so click on dropdown item fires first
          setTimeout(() => onFocusLock(false), 200);
        }}
        disabled={aiLoading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Chip
                size="small"
                icon={cfg.icon}
                label={cfg.label}
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: `${cfg.color}20`,
                  color: cfg.color,
                  border: `1px solid ${cfg.color}40`,
                  "& .MuiChip-icon": {color: cfg.color, ml: "4px"},
                  "& .MuiChip-label": {px: "6px"},
                  transition: "all .2s",
                }}
              />
            </InputAdornment>
          ),
          endAdornment: aiLoading ? (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ) : null,
        }}
      />

      {/* Inline search dropdown */}
      {showDropdown && (mode === "search" || mode === "ai") ? (
        <SearchDropdown
          items={dropdownItems}
          loading={searchLoading}
          term={value.trim()}
          activeIndex={activeIndex}
          onAction={executeAction}
        />
      ) : null}

      {/* Inline AI response */}
      {aiResponse ? (
        <AiResponseCard
          response={aiResponse}
          onAdd={async (id) => { setAiResponse(null); await onAddProductById(id); }}
          onAddUnregistered={(name, price) => { setAiResponse(null); onAddUnregistered(name, price); }}
          onClose={() => setAiResponse(null)}
        />
      ) : null}

      {/* Secondary actions */}
      <Stack direction="row" spacing={1} sx={{mt: 1.25}}>
        <Button
          size="small"
          variant="text"
          onClick={onAddOnlyPrice}
          sx={{fontSize: 12, color: "text.secondary"}}
        >
          💲 Apenas valor
        </Button>
        <IconButton
          size="small"
          title="Comando de IA"
          onClick={() => {
            if (mode !== "ai") {
              handleChange("o que posso oferecer? ");
            }
          }}
          sx={{color: "#0d9488", opacity: 0.7, "&:hover": {opacity: 1}}}
        >
          <AutoAwesomeIcon sx={{fontSize: 16}} />
        </IconButton>
      </Stack>
    </Box>
  );
};
